import { useMemo } from 'react';

import type {
  AcceptSidekickConsent,
  EnsureSidekickConversation,
  GetSidekickConsentStatus,
  SubmitSidekickFeedback,
} from '../contexts/SidekickApiContext';
import { useDefaultConsentBridge } from './useDefaultConsentBridge';
import { useDefaultEnsureConversation } from './useDefaultEnsureConversation';
import { useDefaultSubmitFeedback } from './useDefaultSubmitFeedback';

/** Host-supplied callbacks, each optional, plus the identity consent is keyed on. */
export type SidekickPackageApiParams = {
  ensureConversation?: EnsureSidekickConversation;
  submitFeedback?: SubmitSidekickFeedback;
  getConsentStatus?: GetSidekickConsentStatus;
  acceptConsent?: AcceptSidekickConsent;
  /** Signed-in user identifier used as the consent `profileId`. */
  userId?: string;
};

/** The callbacks a shell hands to `SidekickApiProvider` once defaults are applied. */
export type SidekickPackageApi = {
  ensureConversation: EnsureSidekickConversation;
  submitFeedback: SubmitSidekickFeedback;
  getConsentStatus?: GetSidekickConsentStatus;
  acceptConsent?: AcceptSidekickConsent;
};

/**
 * Resolves the effective Sidekick API callbacks, filling any the host
 * did not supply with package-owned defaults backed by the package's
 * generated client. A host callback always wins, so an app that owns
 * conversation creation or feedback keeps its own implementation.
 *
 * Consent resolves as a pair rather than per-callback: if the host
 * supplies either side, its values pass through untouched (including a
 * partial injection, which `useConsentApi` then degrades to
 * localStorage). Substituting the package default for a host's missing
 * half would silently read and write across two sources of truth.
 *
 * @param params - Host-supplied callbacks and the signed-in user identifier.
 * @returns The callbacks to hand to `SidekickApiProvider`.
 */
export const useSidekickPackageApi = ({
  ensureConversation,
  submitFeedback,
  getConsentStatus,
  acceptConsent,
  userId,
}: SidekickPackageApiParams): SidekickPackageApi => {
  const defaultEnsureConversation = useDefaultEnsureConversation();
  const defaultSubmitFeedback = useDefaultSubmitFeedback();
  const defaultConsent = useDefaultConsentBridge(userId);
  const hasHostConsent =
    getConsentStatus !== undefined || acceptConsent !== undefined;

  return useMemo(
    () => ({
      ensureConversation: ensureConversation ?? defaultEnsureConversation,
      submitFeedback: submitFeedback ?? defaultSubmitFeedback,
      ...(hasHostConsent
        ? { getConsentStatus, acceptConsent }
        : defaultConsent),
    }),
    [
      acceptConsent,
      defaultConsent,
      defaultEnsureConversation,
      defaultSubmitFeedback,
      ensureConversation,
      getConsentStatus,
      hasHostConsent,
      submitFeedback,
    ]
  );
};
